import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";

const EducationMasterForm = ({
  title,
  backPath,
  submitLabel,
  addEndpoint,
  getEndpoint,
  updateEndpoint,
  responseKey,
  includeCategory = false,
}) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(slug);
  const [loading, setLoading] = useState(isEditMode);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    educationCategory: "",
    status: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (includeCategory) {
          const categoryResponse = await axiosInstance.get(
            "/get-all-education-category"
          );
          setCategories(categoryResponse.data?.EducationCategory || []);
        }

        if (isEditMode) {
          const { data } = await axiosInstance.get(`${getEndpoint}/${slug}`);
          const item = data?.[responseKey] || {};
          setFormData({
            name: item.name || "",
            educationCategory: item.educationCategory || "",
            status: Boolean(item.status),
          });
        }
      } catch (error) {
        toast.error("Unable to load education data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getEndpoint, includeCategory, isEditMode, responseKey, slug]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const submitData = async () => {
    setSubmitLoading(true);
    try {
      if (isEditMode) {
        await axiosInstance.put(`${updateEndpoint}/${slug}`, formData);
        toast.success(`${title} updated successfully!`);
      } else {
        await axiosInstance.post(addEndpoint, formData);
        toast.success(`${title} added successfully!`);
      }
      navigate(backPath);
    } catch (error) {
      toast.error(error.response?.data?.message || `Unable to save ${title}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4 d-flex justify-content-between w-100 flex-wrap">
          <div>
            <h1 className="h4">{isEditMode ? `Edit ${title}` : `Add ${title}`}</h1>
          </div>
          <Link to={backPath} className="btn btn-primary">
            Go back
          </Link>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="row">
            <div className="col-md-8 mb-4">
              <div className="card border-0 shadow components-section">
                <div className="card-body">
                  <div className="mb-4">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      value={formData.name}
                      name="name"
                      onChange={handleChange}
                    />
                  </div>
                  {includeCategory && (
                    <div className="mb-4">
                      <label htmlFor="educationCategory">
                        Education Category
                      </label>
                      <select
                        className="form-select"
                        id="educationCategory"
                        name="educationCategory"
                        value={formData.educationCategory}
                        onChange={handleChange}
                      >
                        <option value="">Select Education Category</option>
                        {categories.map((category) => (
                          <option key={category._id} value={category.name}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-4">
              <div className="card border-0 shadow components-section">
                <div className="card-body">
                  <div className="row">
                    <div className="col-4">
                      <label className="my-1 me-2" htmlFor="status">
                        Status
                      </label>
                    </div>
                    <div className="col-8">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="status"
                          id="status"
                          checked={formData.status}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-secondary mt-3"
                    type="button"
                    onClick={submitData}
                    disabled={submitLoading}
                  >
                    {submitLoading ? "Loading..." : submitLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default EducationMasterForm;
